import * as React from 'react';
import { KitPatternVariant, CollarStyle } from 'types/kitLab';
import { outlineStrokeProps } from 'helpers/kitLab';

interface Props {
  variant: KitPatternVariant;
  primaryColor: string;
  secondaryColor: string;
  showPocket?: boolean;
  showCollar?: boolean;
  showSleeves?: boolean;
  showShoulderTriangles?: boolean;
  showSleeveCuffs?: boolean;

  useTrimColor?: boolean;
  trimColor?: string;

  stripeCount?: number;
  stripesEqualWidth?: boolean;
  stripePattern?: number[];

  diagonalDirection?: 'LEFT' | 'RIGHT';
  chevronSize?: 'SMALL' | 'BIG';
  chevronDirection?: 'UP' | 'DOWN';
  chevronPosition?: 'TOP' | 'MIDDLE' | 'BOTTOM';

  sideTrianglesMode?: 'TOP' | 'BOTTOM' | 'BOTH';

  pocketColor?: string;
  sleevesColor?: string;
  collarColor?: string;
  shoulderTrianglesColor?: string;
  sleeveCuffsColor?: string;

  collarStyle?: CollarStyle;
  sleevePattern?: 'PLAIN' | 'TOP_STRIPE' | 'TOP_TRIANGLES';

  className?: string;
}

export const KitShirtPreview: React.FC<Props> = ({
  variant,
  primaryColor,
  secondaryColor,
  showPocket = true,
  showCollar = true,
  showSleeves = true,
  useTrimColor = false,
  trimColor,
  stripeCount,
  stripesEqualWidth = true,
  stripePattern,
  diagonalDirection = 'RIGHT',
  chevronSize = 'SMALL',
  chevronPosition = 'MIDDLE',
  chevronDirection = 'DOWN',
  sideTrianglesMode = 'BOTH',
  showShoulderTriangles = false,
  showSleeveCuffs = false,
  pocketColor,
  sleevesColor,
  collarColor,
  shoulderTrianglesColor,
  sleeveCuffsColor,
  collarStyle = 'ROUND',
  sleevePattern = 'PLAIN',
  className
}) => {
  const effectiveTrimColor = useTrimColor && trimColor ? trimColor : secondaryColor || primaryColor;

  const sleevesFillColor = sleevesColor || effectiveTrimColor;
  const pocketFillColor = pocketColor || secondaryColor || primaryColor;
  const collarFillColor = collarColor || effectiveTrimColor;

  const bodyRect = { x: 40, y: 40, width: 40, height: 70 };
  const bodyRight = bodyRect.x + bodyRect.width;
  const bodyBottom = bodyRect.y + bodyRect.height;

  const shoulderDrop = 4;
  const shoulderInset = 6;
  const shoulderRadius = 0.6;

  const bodyPathD = `
    M ${bodyRect.x},${bodyBottom}
    L ${bodyRect.x},${bodyRect.y + shoulderDrop + shoulderRadius}
    Q ${bodyRect.x},${bodyRect.y + shoulderDrop} ${bodyRect.x + shoulderInset},${bodyRect.y}
    L ${bodyRight - shoulderInset},${bodyRect.y}
    Q ${bodyRight},${bodyRect.y + shoulderDrop} ${bodyRight},${bodyRect.y + shoulderDrop + shoulderRadius}
    L ${bodyRight},${bodyBottom}
    Z
  `;

  const underArmY = bodyRect.y + 32;

  const svgClassName = ['eKitLabShirtPreview', className].filter(Boolean).join(' ');

  const neckCenterX = bodyRect.x + bodyRect.width / 2;
  const neckCenterY = bodyRect.y + 2;
  const neckRadius = 8;

  const bodyClipId = 'kitlab-shirt-body-clip';
  const neckMaskId = 'kitlab-shirt-neck-mask';
  const roundCollarClipId = 'kitlab-shirt-round-collar-clip';

  const getStripeCount = (): number => {
    if (!stripeCount || stripeCount < 2) return 5;
    if (stripeCount > 12) return 12;
    return Math.floor(stripeCount);
  };

  const getStripeRatios = (): number[] => {
    const count = getStripeCount();

    if (stripesEqualWidth) {
      return Array(count).fill(1);
    }

    if (stripePattern && stripePattern.length > 0) {
      const ratios = stripePattern.slice(0, count);
      while (ratios.length < count) ratios.push(1);
      return ratios;
    }

    return Array.from({ length: count }, (_, i) => (i % 2 === 0 ? 2 : 1));
  };

  const renderPattern = () => {
    switch (variant) {
      case 'verticalStripes': {
        const ratios = getStripeRatios();
        const totalUnits = ratios.reduce((s, r) => s + r, 0);
        const unitWidth = bodyRect.width / totalUnits;
        let currentX = bodyRect.x;

        return (
          <g clipPath={`url(#${bodyClipId})`}>
            {ratios.map((ratio, index) => {
              const width = ratio * unitWidth;
              const rect = (
                <rect
                  key={index}
                  x={currentX}
                  y={bodyRect.y}
                  width={width}
                  height={bodyRect.height}
                  fill={index % 2 === 0 ? secondaryColor : primaryColor}
                />
              );
              currentX += width;
              return rect;
            })}
          </g>
        );
      }

      case 'horizontalStripes': {
        const ratios = getStripeRatios();
        const totalUnits = ratios.reduce((s, r) => s + r, 0);
        const unitHeight = bodyRect.height / totalUnits;
        let currentY = bodyRect.y;

        return (
          <g clipPath={`url(#${bodyClipId})`}>
            {ratios.map((ratio, index) => {
              const height = ratio * unitHeight;
              const rect = (
                <rect
                  key={index}
                  x={bodyRect.x}
                  y={currentY}
                  width={bodyRect.width}
                  height={height}
                  fill={index % 2 === 0 ? secondaryColor : primaryColor}
                />
              );
              currentY += height;
              return rect;
            })}
          </g>
        );
      }

      case 'diagonal': {
        const ratios = getStripeRatios();
        const totalUnits = ratios.reduce((s, r) => s + r, 0);

        const isRight = diagonalDirection === 'RIGHT';

        const x0 = bodyRect.x;
        const y0 = bodyRect.y;
        const w = bodyRect.width;
        const h = bodyRect.height;

        const getR = (x: number, y: number) => (isRight ? x - y : x + y);
        const getS = (x: number, y: number) => (isRight ? x + y : x - y);

        const rValues = [getR(x0, y0), getR(x0 + w, y0), getR(x0, y0 + h), getR(x0 + w, y0 + h)];
        const sValues = [getS(x0, y0), getS(x0 + w, y0), getS(x0, y0 + h), getS(x0 + w, y0 + h)];

        const rMin = Math.min(...rValues);
        const rMax = Math.max(...rValues);
        const sMin = Math.min(...sValues);
        const sMax = Math.max(...sValues);

        const unitR = (rMax - rMin) / totalUnits;
        const margin = w + h;
        const sStart = sMin - margin;
        const sEnd = sMax + margin;

        const toXY = (s: number, r: number) => {
          if (isRight) {
            return {
              x: (s + r) / 2,
              y: (s - r) / 2
            };
          }
          return {
            x: (r + s) / 2,
            y: (r - s) / 2
          };
        };

        let currentR = rMin;
        const stripes: JSX.Element[] = [];

        for (let i = 0; i < ratios.length; i += 1) {
          const ratio = ratios[i];
          const r0 = currentR;
          const r1 = currentR + ratio * unitR;
          currentR = r1;

          const p1 = toXY(sStart, r0);
          const p2 = toXY(sEnd, r0);
          const p3 = toXY(sEnd, r1);
          const p4 = toXY(sStart, r1);

          const points = `${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y} ${p4.x},${p4.y}`;

          stripes.push(<polygon key={i} points={points} fill={i % 2 === 0 ? secondaryColor : primaryColor} />);
        }

        return <g clipPath={`url(#${bodyClipId})`}>{stripes}</g>;
      }

      case 'sideTriangles': {
        const { x, y, width, height } = bodyRect;
        const right = x + width;
        const bottom = y + height;

        const topWidth = 12;
        const topHeight = 18;
        const bottomWidth = 12;
        const bottomHeight = 18;

        const triangles: JSX.Element[] = [];

        if (sideTrianglesMode === 'TOP' || sideTrianglesMode === 'BOTH') {
          triangles.push(
            <polygon
              key="side-top-left"
              points={`${x},${y} ${x + topWidth},${y} ${x},${y + topHeight}`}
              fill={secondaryColor}
            />
          );

          triangles.push(
            <polygon
              key="side-top-right"
              points={`${right - topWidth},${y} ${right},${y} ${right},${y + topHeight}`}
              fill={secondaryColor}
            />
          );
        }

        if (sideTrianglesMode === 'BOTTOM' || sideTrianglesMode === 'BOTH') {
          triangles.push(
            <polygon
              key="side-bottom-left"
              points={`${x},${bottom - bottomHeight} ${x + bottomWidth},${bottom} ${x},${bottom}`}
              fill={secondaryColor}
            />
          );

          triangles.push(
            <polygon
              key="side-bottom-right"
              points={`${right - bottomWidth},${bottom} ${right},${bottom} ${right},${bottom - bottomHeight}`}
              fill={secondaryColor}
            />
          );
        }

        return <g clipPath={`url(#${bodyClipId})`}>{triangles}</g>;
      }

      case 'sidePanels': {
        const { x, y, width, height } = bodyRect;
        const right = x + width;
        const bottom = y + height;

        const panelWidth = 4;
        const panelColor = secondaryColor;

        const leftBottomX1 = x;
        const leftBottomX2 = x + panelWidth;

        const rightBottomX2 = right;
        const rightBottomX1 = right - panelWidth;

        const topYOuter = underArmY + 1;
        const topYInner = underArmY + 3;

        const leftPathD = `
          M ${leftBottomX1} ${bottom}
          L ${leftBottomX2} ${bottom}
          L ${leftBottomX2} ${topYInner}
          L ${leftBottomX1} ${topYOuter}
          Z
        `;

        const rightPathD = `
          M ${rightBottomX2} ${bottom}
          L ${rightBottomX1} ${bottom}
          L ${rightBottomX1} ${topYInner}
          L ${rightBottomX2} ${topYOuter}
          Z
        `;

        return (
          <g clipPath={`url(#${bodyClipId})`}>
            <path d={leftPathD} fill={panelColor} />
            <path d={rightPathD} fill={panelColor} />
          </g>
        );
      }

      case 'sideThinStripes': {
        const { x, y, width, height } = bodyRect;
        const right = x + width;
        const bottom = y + height;

        const stripeWidth = 3;
        const topY = y;
        const bottomY = bottom - 1;

        return (
          <g clipPath={`url(#${bodyClipId})`}>
            <rect x={x} y={topY} width={stripeWidth} height={bottomY - topY} fill={secondaryColor} />
            <rect x={right - stripeWidth} y={topY} width={stripeWidth} height={bottomY - topY} fill={secondaryColor} />
          </g>
        );
      }

      case 'sideSemiCircles': {
        const { x, y, width, height } = bodyRect;
        const bottom = y + height;

        const semiTopY = underArmY;
        const semiBottomY = bottom;
        const semiHeight = semiBottomY - semiTopY;

        const centerY = semiTopY + semiHeight / 2;

        const rx = 12;
        const ry = semiHeight / 2;

        const leftCx = x;
        const rightCx = x + width;

        return (
          <g clipPath={`url(#${bodyClipId})`}>
            <ellipse cx={leftCx} cy={centerY} rx={rx} ry={ry} fill={secondaryColor} />
            <ellipse cx={rightCx} cy={centerY} rx={rx} ry={ry} fill={secondaryColor} />
          </g>
        );
      }

      case 'chevron': {
        const midX = bodyRect.x + bodyRect.width / 2;
        const w = bodyRect.width;
        const h = bodyRect.height;
        const top = bodyRect.y;

        const isBig = chevronSize === 'BIG';

        const leftX = isBig ? bodyRect.x : bodyRect.x + w * 0.18;
        const rightX = isBig ? bodyRect.x + w : bodyRect.x + w - w * 0.18;

        let centerRatio: number;
        switch (chevronPosition) {
          case 'TOP':
            centerRatio = 0.25;
            break;
          case 'BOTTOM':
            centerRatio = 0.75;
            break;
          case 'MIDDLE':
          default:
            centerRatio = 0.5;
            break;
        }

        const centerY = top + h * centerRatio;
        const bandHalf = h * (isBig ? 0.18 : 0.13);
        const topOuterY = centerY - bandHalf;
        const innerTopY = centerY - bandHalf * 0.1;
        const outerBottomY = centerY + bandHalf * 0.1;
        const innerBottomY = centerY + bandHalf * 1.1;

        let points: { x: number; y: number }[] = [
          { x: leftX, y: topOuterY },
          { x: midX, y: innerTopY },
          { x: rightX, y: topOuterY },
          { x: rightX, y: outerBottomY },
          { x: midX, y: innerBottomY },
          { x: leftX, y: outerBottomY }
        ];

        if (chevronDirection === 'UP') {
          const ys = points.map(p => p.y);
          const yMin = Math.min(...ys);
          const yMax = Math.max(...ys);
          const midY = (yMin + yMax) / 2;

          points = points.map(p => ({
            x: p.x,
            y: 2 * midY - p.y
          }));
        }

        const pathD = 'M ' + points.map((p, i) => `${i === 0 ? '' : 'L '}${p.x} ${p.y}`).join(' ') + ' Z';

        return (
          <g clipPath={`url(#${bodyClipId})`}>
            <path d={bodyPathD} fill={primaryColor} stroke="none" />
            <path d={pathD} fill={secondaryColor} />
          </g>
        );
      }

      case 'plain':
      default:
        return null;
    }
  };

  const renderShoulderTriangles = () => {
    if (!showShoulderTriangles) return null;

    const { x, y, width } = bodyRect;
    const right = x + width;

    const strapTopY = y;
    const strapHeight = 5;

    const neckGap = 6;

    const leftOuterX = x;
    const leftInnerX = neckCenterX - neckGap;

    const rightInnerX = neckCenterX + neckGap;
    const rightOuterX = right;

    const strapColor = shoulderTrianglesColor || secondaryColor;

    return (
      <g clipPath={`url(#${bodyClipId})`}>
        <rect
          x={leftOuterX}
          y={strapTopY}
          width={leftInnerX - leftOuterX}
          height={strapHeight}
          fill={strapColor}
          rx={strapHeight / 2}
          ry={strapHeight / 2}
        />
        <rect
          x={rightInnerX}
          y={strapTopY}
          width={rightOuterX - rightInnerX}
          height={strapHeight}
          fill={strapColor}
          rx={strapHeight / 2}
          ry={strapHeight / 2}
        />
      </g>
    );
  };

  const renderSleeves = () => {
    if (!showSleeves) return null;

    const baseColor = sleevePattern === 'PLAIN' ? sleevesFillColor : primaryColor;
    const accentColor = sleevesFillColor;

    const leftBasePoints = '40,44 26,58 40,72';
    const rightBasePoints = '80,44 94,58 80,72';

    if (sleevePattern === 'PLAIN') {
      return (
        <>
          <polygon points={leftBasePoints} fill={baseColor} />
          <polygon points={rightBasePoints} fill={baseColor} />
          <polygon points={leftBasePoints} fill="none" {...outlineStrokeProps} />
          <polygon points={rightBasePoints} fill="none" {...outlineStrokeProps} />
        </>
      );
    }

    if (sleevePattern === 'TOP_STRIPE') {
      return (
        <>
          <polygon points={leftBasePoints} fill={baseColor} />
          <polygon points={rightBasePoints} fill={baseColor} />

          <polygon
            points="
              40,44
              26,58
              28,60
              40,46
            "
            fill={accentColor}
          />
          <polygon
            points="
              80,44
              94,58
              92,60
              80,46
            "
            fill={accentColor}
          />
          <polygon points={leftBasePoints} fill="none" {...outlineStrokeProps} />
          <polygon points={rightBasePoints} fill="none" {...outlineStrokeProps} />
        </>
      );
    }

    return (
      <>
        <polygon points={leftBasePoints} fill={baseColor} />
        <polygon points={rightBasePoints} fill={baseColor} />

        <polygon
          points="
            40,44
            26,58
            32,58
          "
          fill={accentColor}
        />
        <polygon
          points="
            80,44
            88,58
            94,58
          "
          fill={accentColor}
        />
        <polygon points={leftBasePoints} fill="none" {...outlineStrokeProps} />
        <polygon points={rightBasePoints} fill="none" {...outlineStrokeProps} />
      </>
    );
  };

  const renderSleeveCuffs = () => {
    if (!showSleeves || !showSleeveCuffs) return null;

    const cuffColor = sleeveCuffsColor || secondaryColor || sleevesFillColor;

    return (
      <g>
        <path d="M 27 59 L 39 71" stroke={cuffColor} strokeWidth={2.5} strokeLinecap="round" />
        <path d="M 81 71 L 93 59" stroke={cuffColor} strokeWidth={2.5} strokeLinecap="round" />
      </g>
    );
  };

  const renderRoundCollar = () => {
    if (!showCollar || collarStyle !== 'ROUND') return null;

    const ringWidth = 2;
    const r = neckRadius - ringWidth / 2;

    return (
      <g clipPath={`url(#${roundCollarClipId})`}>
        <circle
          cx={neckCenterX}
          cy={neckCenterY}
          r={r}
          fill="none"
          stroke={collarFillColor}
          strokeWidth={ringWidth}
          strokeLinecap="round"
        />
      </g>
    );
  };

  const renderPoloCollar = () => {
    if (!showCollar || collarStyle !== 'POLO') return null;

    const color = collarFillColor;

    const cx = neckCenterX;
    const cy = neckCenterY;
    const r = neckRadius;

    const shoulderY = bodyRect.y;
    const leftShoulderX = bodyRect.x;
    const rightShoulderX = bodyRect.x + bodyRect.width;

    const neckBottomY = cy + r * 1.25;
    const apexY = neckBottomY;

    const leftApexX = cx;
    const rightApexX = cx;

    const outerOffset = 5;
    const innerOffset = 8;

    const leftOuterTopX = leftShoulderX + outerOffset;
    const leftInnerTopX = cx - innerOffset;
    const rightInnerTopX = cx + innerOffset;
    const rightOuterTopX = rightShoulderX - outerOffset;

    return (
      <>
        <polygon
          points={`
            ${leftOuterTopX},${shoulderY}
            ${leftInnerTopX},${shoulderY}
            ${leftApexX},${apexY}
          `}
          fill={color}
        />
        <polygon
          points={`
            ${rightInnerTopX},${shoulderY}
            ${rightOuterTopX},${shoulderY}
            ${rightApexX},${apexY}
          `}
          fill={color}
        />
      </>
    );
  };

  const renderBody = () => (
    <>
      <g mask={`url(#${neckMaskId})`}>
        <path d={bodyPathD} fill={primaryColor} stroke="none" />
        {renderPattern()}
        {renderShoulderTriangles()}
        {showPocket && (
          <g>
            <rect
              x={bodyRect.x + bodyRect.width - 14}
              y={bodyRect.y + 14}
              width={12}
              height={14}
              rx={2}
              ry={2}
              fill={pocketFillColor}
              stroke={primaryColor}
              strokeWidth={1.2}
            />
            <path
              d={`
                M ${bodyRect.x + bodyRect.width - 14} ${bodyRect.y + 19}
                L ${bodyRect.x + bodyRect.width - 8}  ${bodyRect.y + 23}
                L ${bodyRect.x + bodyRect.width - 2}  ${bodyRect.y + 19}
              `}
              fill="none"
              stroke={primaryColor}
              strokeWidth={1}
            />
          </g>
        )}
      </g>

      <path d={bodyPathD} fill="none" mask={`url(#${neckMaskId})`} {...outlineStrokeProps} />
    </>
  );

  return (
    <svg
      className={svgClassName}
      viewBox="0 0 120 120"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <clipPath id={bodyClipId}>
          <path d={bodyPathD} />
        </clipPath>
        <mask id={neckMaskId} maskUnits="userSpaceOnUse">
          <rect x="0" y="0" width="120" height="120" fill="black" />
          <path d={bodyPathD} fill="white" />
          <circle cx={neckCenterX} cy={neckCenterY} r={neckRadius} fill="black" />
        </mask>
        <clipPath id={roundCollarClipId}>
          <path d={bodyPathD} />
          <rect x="0" y={neckCenterY} width="120" height="120" />
        </clipPath>
      </defs>
      {renderSleeves()}
      {renderSleeveCuffs()}
      {renderBody()}
      {renderRoundCollar()}
      {renderPoloCollar()}
    </svg>
  );
};
